import { useMutation } from "@apollo/client";
import CurrencyExchangeOutlinedIcon from "@mui/icons-material/CurrencyExchangeOutlined";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import BankIcon from "../../../assets/icons/BankIcon";
import CardIcon from "../../../assets/icons/CardIcon";
import paymentSuccessIcon from "../../../assets/icons/Payment Success Web.svg";
import {
  ENABLE_AUTOPAY_CARD_DESCRIPTION,
  ENABLE_AUTOPAY_CARD_TITLE,
  ENABLE_AUTOPAY_CTA,
  GO_TO_BILLING_BTN,
  SAVE_PAYMENT_METHOD_DESCRIPTION,
  SAVE_PAYMENT_METHOD_TITLE,
  SAVE_PAYMENT_METHOD_YES_BTN,
  SUCCESS_PAYMENT_MESSAGE,
  TOKENIZED_BANK,
} from "../../../Constants/constants";
import { MAKE_A_PAYMENT } from "../../../Pages/Payment/query";
import { formatNumberWithCommas, logEvents } from "../../../Services/helper";
import {
  BlankScreenStack,
  CardHeading,
  CenterStack,
  ContainedButton,
  IconSmall,
  OutlinedButton,
  PageGrid,
  StickyBox,
  StyledLink,
} from "../../../Styles/GlobalStyles";
import { Theme } from "../../../Styles/GlobalTheme";
import Button from "../../Button/Button";
import { AccountCard } from "../AccountCard/AccountCard";
import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";
import { NeedHelpCard } from "../NeedHelpCard/NeedHelpCard";
import { SuccessMessageModel } from "../SuccessMessageModel/SuccessMessageModel";
import { UpdateErrorComponent } from "../UpdateErrorComponent/UpdateErrorComponent";

type SaveAction = "saveOnly" | "enrollAutoPay";

export const SuccessFailure = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { scrollPosition } = useSelector((state: any) => state.pageInfo);
  const { anotherAmount } = useSelector((state: any) => state.paymentDetails);
  const { paidAmount } = useSelector((state: any) => state.customerPlanDetails);
  const { accountNumber, activeAccount } = useSelector((state: any) => state.customerInfoDetails);
  const { autoPayData } = useSelector((state: any) => state.autopayPaperlessDetails);
  const [transactionId, setTransactionId] = useState<string>("");
  const [error, setError] = useState<boolean>(false);
  const [makePaymentInput, setMakePaymentInput] = useState<any>(null);
  const [saveCardDismissed, setSaveCardDismissed] = useState<boolean>(false);
  const [saveInProgress, setSaveInProgress] = useState<boolean>(false);
  const [paymentSaved, setPaymentSaved] = useState<boolean>(false);
  const [pendingSaveAction, setPendingSaveAction] = useState<SaveAction | null>(null);
  const [openError, setOpenError] = useState<boolean>(false);
  const [openSuccess, setOpenSuccess] = useState<boolean>(false);

  const paymentAmountInPositive = paidAmount && !paidAmount?.includes("-");

  const isNewPaymentMethod = location?.state?.isNewPaymentMethod;
  const isEnrolledInAutopay = !!(autoPayData?.paymentMethodId || autoPayData?.paymentMethodName);

  const proceedAfterSave = (saveAction: SaveAction | null) => {
    if (saveAction === "enrollAutoPay") {
      let id = "";
      try {
        const payload = JSON.parse(savePaymentMethodData?.makeAPayment?.message || "{}");
        id = payload?.payment_method?.id ?? "";
      } catch (error) {
        console.log(error);
      }
      navigate("/billing/signupautopay", {
        state: {
          fromPaymentSuccess: true,
          preselectedMethodId: isNewPaymentMethod ? id : makePaymentInput?.paymentMethodId,
        },
      });
      return;
    }

    setSaveCardDismissed(true);
  };
  const [savePaymentMethod, { loading: paymentLoading, data: savePaymentMethodData }] = useMutation(
    MAKE_A_PAYMENT,
    {
      onCompleted: () => {
        setSaveInProgress(false);
        setPaymentSaved(true);
        setOpenSuccess(true);
      },
      onError: () => {
        setSaveInProgress(false);
        setPendingSaveAction(null);
        setOpenError(true);
      },
    }
  );
  const disableSaveActions = paymentLoading || saveInProgress;

  const handleNavigate = () => {
    navigate("/billing/makeapayment");
  };

  const handleSavePaymentMethod = (saveAction: SaveAction) => {
    if (disableSaveActions) return;

    if (paymentSaved || !isNewPaymentMethod) {
      proceedAfterSave(saveAction);
      return;
    }

    setOpenError(false);
    setSaveInProgress(true);
    setPendingSaveAction(saveAction);

    const {
      amount,
      savePaymentInfo,
      surcharge,
      paymentChannel,
      completePayment,
      ...savePymentInput
    } = makePaymentInput || {};

    savePaymentMethod({
      variables: {
        accountNumber,
        makeAPaymentInput: {
          ...savePymentInput,
          savePaymentInfo: true,
          completePayment: false,
        },
      },
    });
  };

  const handleYesSaveIt = () => {
    handleSavePaymentMethod("saveOnly");
  };

  const handleGoToBilling = () => {
    navigate("/billing");
  };

  const handleEnableAutoPay = () => {
    handleSavePaymentMethod("enrollAutoPay");
  };

  const handleCloseSuccess = () => {
    const nextAction = pendingSaveAction;
    setOpenSuccess(false);
    setPendingSaveAction(null);
    proceedAfterSave(nextAction);
  };
  const handleSetIcon = (methodType: string) => {
    switch (true) {
      case methodType === TOKENIZED_BANK:
        return (
          <BankIcon
            sx={{
              color: Theme.palette.primary.main,
              width: 20,
              height: 20,
            }}
          />
        );
      default:
        return (
          <CardIcon
            sx={{
              color: Theme.palette.primary.main,
              width: 20,
              height: 20,
            }}
          />
        );
    }
  };

  useEffect(() => {
    const hasError = Boolean(location?.state?.error);
    setTransactionId(location?.state?.data);
    setError(hasError);
    if (location?.state?.isNewPaymentMethod) {
      setMakePaymentInput(location?.state?.makePaymentInput ?? null);
    } else {
      setMakePaymentInput(location?.state?.scheduleOneTimePaymentInput ?? null);
    }
    hasError ? logEvents("PAYMENT_FAILED") : logEvents("PAYMENT_SUCCESS");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  return (
    <>
      <PageGrid container spacing={1}>
        <Grid item xs={12} lg={8} mt={{ md: 4 }}>
          <BlankScreenStack>
            {!error ? (
              <>
                <IconSmall src={paymentSuccessIcon} alt="success" />
                <CardHeading my={3} data-testid="success-msg">
                  Payment successful
                </CardHeading>
                {transactionId?.trim()?.length > 0 && (
                  <Typography fontWeight={600} component={"p"} fontSize={"12px"}>
                    Transaction ID:&nbsp; {transactionId}
                  </Typography>
                )}
                <CenterStack textAlign={"center"}>
                  {(anotherAmount || paymentAmountInPositive) && (
                    <Typography>{`Your transaction of ${formatNumberWithCommas(
                      anotherAmount > 0 ? anotherAmount : paidAmount
                    )} was successful.`}</Typography>
                  )}
                  <Typography>You can track all your payments in Transaction History.</Typography>
                </CenterStack>
                <Typography
                  textAlign={"center"}
                  my={2}
                  color={Theme.palette.customcolor.customText}
                  fontSize={"14px"}>
                  Note : It may take time for the information to reflect in Transaction History.
                </Typography>

                <Stack spacing={2} width="100%" maxWidth={760} mt={1}>
                  {!isEnrolledInAutopay && activeAccount && (
                    <Box
                      sx={{
                        width: "100%",
                        backgroundColor:
                          Theme.palette.customcolor.saveAutoPayPaperColor ?? "#E5F2FF",
                        borderRadius: 2,
                        p: { xs: 2, sm: 3 },
                      }}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", sm: "center" }}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Box
                            sx={{
                              backgroundColor: Theme.palette.customcolor.whiteColor,
                              padding: "8px 10px 6px 10px",
                              borderRadius: "8px",
                            }}>
                            <CurrencyExchangeOutlinedIcon color="success" sx={{ mt: "2px" }} />
                          </Box>
                          <Box>
                            <Typography fontWeight={700} fontSize={{ xs: "18px" }}>
                              {ENABLE_AUTOPAY_CARD_TITLE}
                            </Typography>
                            <Typography
                              fontSize={"12px"}
                              color={Theme.palette.customcolor.customText}
                              mt={0.5}>
                              {ENABLE_AUTOPAY_CARD_DESCRIPTION}
                            </Typography>
                          </Box>
                        </Stack>
                        <ContainedButton
                          sx={{ alignSelf: "center" }}
                          onClick={handleEnableAutoPay}
                          disabled={disableSaveActions}>
                          {ENABLE_AUTOPAY_CTA}
                        </ContainedButton>
                      </Stack>
                    </Box>
                  )}

                  {isNewPaymentMethod && !saveCardDismissed ? (
                    <Box
                      sx={{
                        width: "100%",
                        backgroundColor:
                          Theme.palette.customcolor.saveAutoPayPaperColor ?? "#E5F2FF",
                        borderRadius: 2,
                        p: { xs: 2, sm: 3 },
                      }}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", sm: "center" }}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Box
                            sx={{
                              backgroundColor: Theme.palette.customcolor.whiteColor,
                              padding: "10px 10px 6px 10px",
                              borderRadius: "8px",
                            }}>
                            {handleSetIcon(makePaymentInput?.paymentMethodType)}
                          </Box>
                          <Box>
                            <Typography fontWeight={700} fontSize={{ xs: "18px" }}>
                              {SAVE_PAYMENT_METHOD_TITLE}
                            </Typography>
                            <Typography
                              fontSize={"12px"}
                              color={Theme.palette.customcolor.customText}
                              mt={0.5}>
                              {SAVE_PAYMENT_METHOD_DESCRIPTION}
                            </Typography>
                          </Box>
                        </Stack>
                        <Stack spacing={1.5} sx={{ alignSelf: "center" }}>
                          <ContainedButton onClick={handleYesSaveIt} disabled={disableSaveActions}>
                            {SAVE_PAYMENT_METHOD_YES_BTN}
                          </ContainedButton>
                          <OutlinedButton onClick={handleGoToBilling} disabled={disableSaveActions}>
                            {GO_TO_BILLING_BTN}
                          </OutlinedButton>
                        </Stack>
                      </Stack>
                    </Box>
                  ) : (
                    <StyledLink to={"/billing"} sx={{ alignSelf: "center" }}>
                      <OutlinedButton>{GO_TO_BILLING_BTN}</OutlinedButton>
                    </StyledLink>
                  )}
                </Stack>
              </>
            ) : (
              <>
                <CardHeading my={3}>Payment failed</CardHeading>
                <Typography component={"p"} px={{ sm: "6rem", md: "10rem" }} textAlign={"center"}>
                  Sorry, we're not able to process your payment at the moment, please try again.
                </Typography>
                <Stack marginTop={6}>
                  <Button type="root" title="BACK" onClick={handleNavigate} />
                </Stack>
              </>
            )}
          </BlankScreenStack>
        </Grid>
        <Grid
          item
          xs={12}
          lg={3.5}
          display={{ xs: "block", lg: scrollPosition > 20 ? "block" : "none" }}>
          <AccountCard />
          <NeedHelpCard />
        </Grid>

        <StickyBox display={{ xs: "none", lg: scrollPosition > 20 ? "none" : "block" }}>
          <AccountCard />
          <NeedHelpCard />
        </StickyBox>
      </PageGrid>

      {openSuccess && (
        <SuccessMessageModel
          successMessage={openSuccess}
          setSuccessMessage={(value: boolean) => {
            if (!value) {
              handleCloseSuccess();
              return;
            }

            setOpenSuccess(value);
          }}
          note={SUCCESS_PAYMENT_MESSAGE}
        />
      )}
      {openError && <UpdateErrorComponent openError={openError} setOpenError={setOpenError} />}

      {(paymentLoading || saveInProgress) && <LoadingSpinner />}
    </>
  );
};
